import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Sparkles } from "lucide-react";
import { usePermissions } from "@/hooks/usePermissions";
import { cn } from "@/lib/utils";
import { useFullscreenOverlayOpen } from "@/lib/fullscreen-overlay";
import { COPILOT_HOME_PATH } from "@/lib/copilot-home";
import { ASSISTANT_PRODUCT_NAME } from "./product";
import { AssistantChatPanel } from "./AssistantChatPanel";
import { useActiveCampaignName } from "./useActiveCampaignName";

/**
 * Floating Copilot-style entry for GuIA.
 * - FAB opens the widget on any page except the full /app/assistant route.
 * - Expand (in panel header) goes to the ChatGPT-style full page.
 */
export function AssistantCopilotWidget() {
  const { can } = usePermissions();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const campaignName = useActiveCampaignName();
  const displayName = campaignName ?? ASSISTANT_PRODUCT_NAME;
  const onFullPage = location.pathname.startsWith("/app/assistant") || location.pathname.startsWith("/app/coordinator/chat") || location.pathname.startsWith("/app/flow-help");
  // The agent editor's own composer (simulator, copilot) sits where the FAB
  // lands, so the button covered the message box.
  const onOwnComposer = /^\/app\/agents\/[^/]+\/nuevo-editor/.test(location.pathname) || location.pathname === COPILOT_HOME_PATH;
  const allowed = can("knowledge_assistants.consult");
  const overlayOpen = useFullscreenOverlayOpen();
  const onInbox = location.pathname.startsWith("/app/conversations");

  useEffect(() => {
    if (onFullPage) setOpen(false);
  }, [onFullPage]);

  if (!allowed || onFullPage || onOwnComposer || overlayOpen) return null;

  return (
    <div className={cn(
      "fixed right-4 z-[60] flex flex-col items-end gap-3 sm:right-6",
      "bottom-20 sm:bottom-24",
    )}>
      {open && (
        <div className="h-[min(640px,78dvh)] w-[min(440px,calc(100vw-2rem))] animate-in fade-in duration-200">
          <AssistantChatPanel
            variant="widget"
            forceProfile="campaign"
            onClose={() => setOpen(false)}
            onMinimize={() => setOpen(false)}
          />
        </div>
      )}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={cn(
          "inline-flex items-center gap-2 rounded-full text-sm font-medium shadow-lg transition-all",
          onInbox ? "h-10 w-10 justify-center" : "px-4 py-2.5",
          "bg-primary text-primary-foreground hover:opacity-95",
          open && "ring-2 ring-primary/30",
        )}
        aria-label={open ? `Cerrar ${displayName}` : `Abrir ${displayName}`}
        title={onInbox ? displayName : undefined}
      >
        <Sparkles className="h-4 w-4" />
        {!onInbox && <span className="hidden sm:inline">{displayName}</span>}
      </button>
    </div>
  );
}
